import { useSelector } from "react-redux";
import {
  getJSCollections,
  getPageActions,
  getPlugins,
} from "ee/selectors/entitiesSelector";
import { getAgentChatQueries } from "ee/selectors/aiAgentSelectors";
import { groupBy, keyBy, sortBy } from "lodash";
import type { FunctionCallingConfigFormToolField } from "components/formControls/FunctionCallingConfigControl/types";
import { useMemo } from "react";
import { getActionConfig } from "pages/Editor/Explorer/Actions/helpers";
import { JsFileIconV2 } from "pages/Editor/Explorer/ExplorerIcons";
import type { ToolGroup, Tool } from "../types";
import { getCurrentPageId } from "selectors/editorSelectors";

export const useTools = (
  selectedTools: FunctionCallingConfigFormToolField[],
): ToolGroup[] => {
  const pageId = useSelector(getCurrentPageId);
  const actions = useSelector(getPageActions(pageId));
  const jsCollections = useSelector(getJSCollections);
  const chatQueries = useSelector(getAgentChatQueries);
  const selectedToolIds = keyBy(selectedTools, "entityId");
  const plugins = useSelector(getPlugins);
  const pluginGroups = useMemo(() => keyBy(plugins, "id"), [plugins]);

  // Filter out chat queries from actions
  const filteredActions = actions.filter((action) => {
    return !chatQueries.some((query) => query.config.id === action.config.id);
  });

  const groupedActions = groupBy(filteredActions, "config.datasource.id");
  const groupedJSCollections = groupBy(jsCollections, "config.id");

  const toolGroups: ToolGroup[] = [];

  // Transform actions into tool groups
  Object.entries(groupedActions).forEach(([datasourceId, actions]) => {
    const tools: Tool[] = actions
      .sort((a, b) => a.config.name.localeCompare(b.config.name))
      .map((action) => {
        const selectedTool = selectedToolIds[action.config.id];

        if (selectedTool) {
          return {
            ...selectedTool,
            id: selectedTool.id,
            name: action.config.name,
            isSelected: true,
            systemGenerated: action.config.systemGenerated,
          };
        }

        return {
          description: "",
          entityId: action.config.id,
          entityType: "Query",
          id: "",
          isApprovalRequired: false,
          isSelected: false,
          name: action.config.name,
          systemGenerated: action.config.systemGenerated,
        };
      });
    const config = getActionConfig(actions[0].config.pluginType);
    const plugin = pluginGroups[actions[0].config.pluginId];
    const icon = config?.getIcon(actions[0], plugin);

    toolGroups.push({
      id: datasourceId,
      icon,
      name: actions[0].config.datasource.name || "Untitled",
      tools,
    });
  });

  // Transform JS collections into tool groups
  Object.entries(groupedJSCollections).forEach(([collectionId, jsActions]) => {
    const tools: Tool[] = jsActions
      .sort((a, b) => a.config.name.localeCompare(b.config.name))
      .flatMap((jsAction) =>
        jsAction.config.actions.map((action) => {
          const selectedTool = selectedToolIds[action.id];

          if (selectedTool) {
            return {
              ...selectedTool,
              id: selectedTool.id,
              name: action.name,
              isSelected: true,
            };
          }

          return {
            description: "",
            entityId: action.id,
            entityType: "JSFunction",
            id: "",
            isApprovalRequired: false,
            isSelected: false,
            name: action.name,
          };
        }),
      );

    toolGroups.push({
      id: collectionId,
      icon: JsFileIconV2(16, 16),
      name: groupedJSCollections[collectionId][0].config.name,
      tools,
    });
  });

  return sortBy(toolGroups, "name");
};
